/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * StockerPort — Stocker의 입출고 포트.
 *
 * 독립 컴포넌트로 모델러에서 자유 배치.
 * targetStocker 속성으로 연결 대상 stocker 지정.
 */

import { Component, ComponentNature, RealObject, RectPath, Shape, sceneComponent } from '@hatiolab/things-scene'
import { StockerPort3d } from './stocker-port-3d.js'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'select',
      label: 'port-type',
      name: 'portType',
      property: {
        options: [
          { display: 'IN', value: 'in' },
          { display: 'OUT', value: 'out' }
        ]
      }
    },
    {
      type: 'id-input',
      label: 'target-stocker',
      name: 'targetStocker',
      property: {
        component: 'stocker'
      }
    },
    {
      type: 'number',
      label: 'depth',
      name: 'depth',
      property: { min: 1 }
    }
  ],
  help: 'scene/component/stocker-port'
}

const COLORS = {
  in: '#27ae60',
  out: '#e67e22',
  empty: '#ddd',
  stroke: '#666',
  carrier: '#4a9eff'
}

interface StockerPortData {
  CARRIER?: string
  STATUS?: string
}

@sceneComponent('stocker-port')
export class StockerPort extends RectPath(Shape) {

  get nature() { return NATURE }


  buildRealObject(): RealObject | undefined {
    return new StockerPort3d(this as any)
  }

  get portType(): 'in' | 'out' {
    return this.getState('portType') || 'in'
  }

  get targetStockerId(): string | undefined {
    return this.getState('targetStocker')
  }

  get targetStocker(): Component | undefined {
    const id = this.targetStockerId
    if (!id) return undefined
    return this.root?.findById?.(id)
  }

  get portData(): StockerPortData {
    return (this.data || {}) as StockerPortData
  }

  get hasCarrier(): boolean {
    return !!this.portData.CARRIER
  }

  // ── 2D Rendering ──

  render(ctx: CanvasRenderingContext2D) {
    const { left, top, width, height } = this.bounds
    const isIn = this.portType === 'in'
    const hasCarrier = this.hasCarrier

    // 배경
    ctx.beginPath()
    ctx.rect(left, top, width, height)
    ctx.fillStyle = hasCarrier ? COLORS.carrier : COLORS.empty
    ctx.fill()

    // 테두리
    ctx.strokeStyle = isIn ? COLORS.in : COLORS.out
    ctx.lineWidth = 2
    ctx.stroke()

    // 방향 표시 (화살표)
    const cx = left + width / 2
    const cy = top + height / 2
    const arrowSize = Math.min(width, height) * 0.3

    ctx.fillStyle = isIn ? COLORS.in : COLORS.out
    ctx.beginPath()
    if (isIn) {
      // 아래 방향 화살표 (IN)
      ctx.moveTo(cx, cy + arrowSize)
      ctx.lineTo(cx - arrowSize * 0.6, cy - arrowSize * 0.3)
      ctx.lineTo(cx + arrowSize * 0.6, cy - arrowSize * 0.3)
    } else {
      // 위 방향 화살표 (OUT)
      ctx.moveTo(cx, cy - arrowSize)
      ctx.lineTo(cx - arrowSize * 0.6, cy + arrowSize * 0.3)
      ctx.lineTo(cx + arrowSize * 0.6, cy + arrowSize * 0.3)
    }
    ctx.closePath()
    ctx.fill()

    // 라벨
    const label = this.state.id || (isIn ? 'IN' : 'OUT')
    ctx.fillStyle = '#333'
    ctx.font = `${Math.min(width * 0.25, height * 0.25, 11)}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillText(label, cx, top + height - 2)
  }
}
